import { BORROWER_BACKGROUND_REVIEW_TYPE, BORROWER_CREDIT_REVIEW_TYPE } from '@roc/feature-utils';
import { Resolution } from '../../types/resolution';
import { findBackgroundSection, stripBorrowerId } from './backgroundResolutionConfig';
import { findCreditItem } from './creditResolutionConfig';
import { DetailRow, parseDetails } from './resolutionDetailsParser';

export type SummaryRow = DetailRow & { flag?: string };

export type ResolutionContent = {
  title: string;
  subtitle: string;
  tableTitle: string;
  rows: SummaryRow[];
  ask?: string;
};

type CreditItemData = {
  accountId?: string;
  accountType?: string;
  balanceAmount?: number;
  pastDue?: number;
  highCreditAmount?: number;
  mostRecentDate?: string;
  mostAgedDate?: string;
  tradelinesCount?: string;
  activeTradeline?: string;
  seasoningTradelines?: string;
  oldestTradeline?: string;
};

const parseItemData = (itemData?: string | null): CreditItemData | null => {
  if (!itemData) return null;
  try {
    return JSON.parse(itemData);
  } catch (e) {
    return null;
  }
};

const formatAmount = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

// Rows from the snapshot saved with the resolution; resolutions created before it existed have none.
// countKey: the itemData key already shown as the count row of the item, skipped here.
const buildItemDataRows = (data: CreditItemData, countKey?: string): SummaryRow[] => {
  const candidates: [string, string, string | undefined][] = [
    ['tradelinesCount', 'Total tradelines', data.tradelinesCount],
    ['activeTradeline', 'Current active tradelines', data.activeTradeline],
    ['seasoningTradelines', 'Seasoned tradelines (open 2+ years)', data.seasoningTradelines],
    ['oldestTradeline', 'Oldest tradeline opened', data.oldestTradeline],
    ['accountType', 'Account type', data.accountType],
    ['accountId', 'Account number', data.accountId ? `Ending in ${data.accountId}` : undefined],
    ['balanceAmount', 'Balance', typeof data.balanceAmount === 'number' ? formatAmount(data.balanceAmount) : undefined],
    ['pastDue', 'Past due', typeof data.pastDue === 'number' ? formatAmount(data.pastDue) : undefined],
    ['highCreditAmount', 'High balance', typeof data.highCreditAmount === 'number' ? formatAmount(data.highCreditAmount) : undefined],
    ['mostRecentDate', 'Most recent late payment', data.mostRecentDate],
    ['mostAgedDate', 'Oldest late payment', data.mostAgedDate],
  ];
  return candidates
    .filter(([key, , value]) => value && value !== '-' && key !== countKey)
    .map(([, label, value]) => ({ label, value: value as string }));
};

const CREDIT_COUNT_LINE = /^\s*Check\s*:\s*(.*?)\s*$/im;

// "PetitionFiled" -> "Petition Filed"; other names are kept as they are.
const humanize = (name: string) => (/^[A-Z][a-z]+([A-Z][a-z]+)+$/.test(name) ? name.replace(/([a-z])([A-Z])/g, '$1 $2') : name);

// maskSensitive: borrower view. Internal users (maskSensitive = false) see SSN, DOB and addresses as they came.
export const buildBackgroundContent = (resolution: Resolution, maskSensitive = true): ResolutionContent => {
  const name = stripBorrowerId(resolution?.itemName);
  const section = findBackgroundSection(name);
  const subtitle = 'Background report';

  if (!section) {
    return {
      title: name,
      subtitle,
      tableTitle: 'Details on the background report',
      rows: parseDetails(resolution?.itemDescription, []).rows,
    };
  }

  const { rows, byLabel } = parseDetails(resolution?.itemDescription, section.fields, { ...section.parseOptions, maskSensitive });
  return {
    title: section.title(byLabel, name),
    subtitle,
    tableTitle: section.tableTitle,
    rows,
    ask: section.ask,
  };
};

export const buildCreditContent = (resolution: Resolution): ResolutionContent => {
  const name = stripBorrowerId(resolution?.itemName);
  const item = findCreditItem(name);
  const details = resolution?.itemDescription || '';
  const subtitle = 'Credit report';

  const rows: SummaryRow[] = [];
  const countMatch = CREDIT_COUNT_LINE.exec(details);
  const countText = countMatch?.[1];
  const count = countText && /^\d+$/.test(countText) ? Number(countText) : null;

  if (count !== null) {
    rows.push({
      label: item?.countLabel || 'Count on the credit report',
      value: String(count),
      flag: item?.minimum !== undefined && count < item.minimum ? `Minimum ${item.minimum}` : undefined,
    });
  } else if (item?.categoryRow) {
    rows.push({ label: 'Account category', value: name });
  }

  const itemData = parseItemData(resolution?.itemData);
  if (itemData) rows.push(...buildItemDataRows(itemData, item?.countKey));

  // Whatever else came in `details` (e.g. original creditor) is kept with its own key.
  const rest = details.replace(CREDIT_COUNT_LINE, '');
  // Public records come as "<borrowerId> Public Record", which is only the record type.
  rows.push(
    ...parseDetails(rest, []).rows.map(row =>
      !row.value && /^\d+\s+Public Record$/i.test(row.label) ? { label: 'Type', value: 'Public record' } : row
    )
  );

  return {
    title: item ? item.title(name) : humanize(name),
    subtitle,
    tableTitle: item?.tableTitle || 'Item on the credit report',
    rows,
    ask: typeof item?.ask === 'function' ? item.ask(name) : item?.ask,
  };
};

export const buildResolutionContent = (resolution?: Resolution, maskSensitive = true): ResolutionContent | null => {
  if (resolution?.objectType === BORROWER_BACKGROUND_REVIEW_TYPE) return buildBackgroundContent(resolution, maskSensitive);
  if (resolution?.objectType === BORROWER_CREDIT_REVIEW_TYPE) return buildCreditContent(resolution);
  return null;
};
